
import { supabase } from "@/integrations/supabase/client";
import { BusinessCategoryString } from "@/types/business";

export interface Category {
  id: BusinessCategoryString;
  name: string;
}

// This will be used for display formatting only, not for restricting category values
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  "FOOD_AND_DRINK": "Food & Drink",
  "RETAIL": "Retail",
  "SERVICES": "Services",
  "ACCOMMODATION": "Accommodation",
  "ENTERTAINMENT": "Entertainment",
  "HEALTH_AND_WELLNESS": "Health & Wellness",
  "EDUCATION": "Education",
  "OTHER": "Other",
  "Accomodation": "Accommodation",
  "Accomodation ": "Accommodation", // Fix for extra space in some DB entries
  "Cafe": "Cafe",
  "Farm": "Farm",
  "Farmers Market": "Farmers Market",
  "Organisation": "Organisation",
  "Restaurant": "Restaurant",
  "Shop": "Shop",
  "Wellness": "Wellness"
};

export const fetchAllCategories = async (): Promise<Category[]> => {
  try {
    console.log("Fetching all available business categories");
    
    // Get unique categories from businesses
    const { data, error } = await supabase
      .from('businesses')
      .select('category')
      .order('category');
    
    if (error) {
      console.error("Error fetching categories:", error);
      throw error;
    }
    
    console.log("Raw categories data from Supabase:", data);
    
    if (!data || data.length === 0) {
      console.log("No categories found in businesses table");
      return [];
    }
    
    // Get unique categories and map them to the Category interface
    const uniqueCategories = [...new Set(data?.map(item => item.category))] as BusinessCategoryString[];
    console.log("Unique categories found:", uniqueCategories);
    
    const categories = uniqueCategories.map(category => ({
      id: category,
      name: CATEGORY_DISPLAY_NAMES[category] || category // Use the display name or the original if not found
    }));
    
    console.log("Processed categories:", categories);
    console.log(`Fetched ${categories.length} categories`);
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

export const fetchPopularTags = async (limit = 10) => {
  try {
    console.log(`Fetching top ${limit} popular tags`);
    
    const { data, error } = await supabase
      .from('businesses')
      .select('tags');
    
    if (error) {
      console.error("Error fetching tags:", error);
      throw error;
    }
    
    // Count tag occurrences
    const tagCounts: Record<string, number> = {};
    data?.forEach(business => {
      if (business.tags && Array.isArray(business.tags)) {
        business.tags.forEach((tag: string) => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    // Convert to array and sort by count
    const sortedTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
      .map(item => item.tag);
    
    console.log(`Found ${sortedTags.length} popular tags`);
    return sortedTags;
  } catch (error) {
    console.error("Error fetching popular tags:", error);
    throw error;
  }
};

/**
 * Format a category name for display
 * @param category The category key to format
 * @returns The formatted category name for display
 */
export const formatCategoryDisplay = (category: BusinessCategoryString): string => {
  return CATEGORY_DISPLAY_NAMES[category] || category;
};

/**
 * Get all categories that have existing businesses
 * @returns An array of category strings
 */
export const getExistingCategories = async (): Promise<string[]> => {
  try {
    console.log("Fetching categories with existing businesses");
    
    const { data, error } = await supabase
      .from('businesses')
      .select('category')
      .order('category');
    
    if (error) {
      console.error("Error fetching existing categories:", error);
      throw error;
    }
    
    // Extract unique categories and ensure they are strings
    const categories = [...new Set(data?.map(item => String(item.category)) || [])] as string[];
    
    console.log(`Found ${categories.length} categories with businesses`);
    return categories;
  } catch (error) {
    console.error("Error fetching existing categories:", error);
    return []; // Return empty array instead of falling back to predefined categories
  }
};
